import { useEffect, useState } from 'react';
import { supportApi } from '../api/Supportapi';
import api from '../api/axios';

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-600',
  assigned: 'bg-amber-50 text-amber-600',
  in_progress: 'bg-purple-50 text-purple-600',
  resolved: 'bg-[#ECFDF5] text-[#0F8B6D]',
  closed: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  new: 'New',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_OPTIONS = ['new', 'assigned', 'in_progress', 'resolved', 'closed'];

const DefaultLayout = ({ children }) => <>{children}</>;

export default function SupportTicketDetailStaff({ ticketId, onBack, Layout }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [remarks, setRemarks] = useState([]);
  const [remarkText, setRemarkText] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);

  const [assigning, setAssigning] = useState(false);

  // Admin-only: assign-to-any-agent modal
  const [allAgents, setAllAgents] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [modalAssigning, setModalAssigning] = useState(false);

  const currentAgent = (() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  const isAdmin = currentAgent?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      api.get('/admin/agents').then((res) => setAllAgents(res.data)).catch(() => {});
    }
  }, [isAdmin]);

  const load = () => {
    setLoading(true);
    supportApi
      .getTicket(ticketId)
      .then(setTicket)
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [ticketId]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await supportApi.replyToTicket(ticketId, message.trim());
      setMessage('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    setError('');
    try {
      await supportApi.updateTicketStatus(ticketId, newStatus);
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!currentAgent?.id) {
      setError('Could not identify current agent — please log in again.');
      return;
    }
    setAssigning(true);
    setError('');
    try {
      await supportApi.updateTicketStatus(ticketId, ticket.status, currentAgent.id);
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Someone just claimed this ticket — refreshing...');
      } else {
        setError(err.response?.data?.message || err.message);
      }
      load(); // refresh so the UI reflects the real assignee
    } finally {
      setAssigning(false);
    }
  };

  const handleModalAssign = async () => {
    if (!selectedAgentId) return;
    setModalAssigning(true);
    setError('');
    try {
      await supportApi.updateTicketStatus(ticketId, ticket.status, Number(selectedAgentId));
      setShowAssignModal(false);
      setSelectedAgentId('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setModalAssigning(false);
    }
  };

  const handleAddRemark = async (e) => {
    e.preventDefault();
    if (!remarkText.trim()) return;
    setAddingRemark(true);
    try {
      await supportApi.addInternalRemark(ticketId, remarkText.trim());
      setRemarkText('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setAddingRemark(false);
    }
  };

  const Wrapper = Layout || DefaultLayout;

  return (
    <Wrapper>
        <div className="bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9] -m-6 p-6 min-h-screen">
    <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-purple-600 mb-5">
          <ArrowLeftIcon />
          Back to all tickets
        </button>

        {loading && <p className="text-sm text-gray-400 py-10 text-center">Loading...</p>}
        {error && <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3 mb-4 border border-red-100">{error}</div>}

        {ticket && (
          <>
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 mb-5">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">{ticket.ticket_number}</span>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mt-1">{ticket.subject}</h3>
                </div>
                <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${STATUS_STYLES[ticket.status]}`}>
                  {STATUS_LABELS[ticket.status]}
                </span>
              </div>
              <div className="text-xs text-gray-400 mb-3 space-y-0.5">
                <p>
                  {ticket.category} · Raised by {ticket.user?.name || 'user'} ({ticket.user_role?.replace('_', ' ')})
                </p>
                <p>
                  {ticket.user?.email && <span>{ticket.user.email}</span>}
                  {ticket.user?.email && ticket.user?.phone && <span> · </span>}
                  {ticket.user?.phone && <span>{ticket.user.phone}</span>}
                </p>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{ticket.description}</p>
            </div>

            {/* Staff controls: status */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 mb-5">
              <p className="text-xs font-semibold text-gray-500 mb-2.5">Update status</p>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    disabled={statusUpdating || ticket.status === s}
                    onClick={() => handleStatusChange(s)}
                    className={`text-xs font-semibold px-4 py-2 rounded-full border transition ${
                      ticket.status === s
                        ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                        : 'bg-white text-gray-500 border-gray-200 hover:bg-purple-50 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>

            {/* Assigned agent */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Assigned agent</p>
                <p className="text-sm font-semibold text-[#0D1B3D]">
                  {ticket.assigned_agent?.name || <span className="text-gray-400 font-normal">Unassigned</span>}
                </p>
              </div>
              {!isAdmin && (
                <button
                  onClick={handleAssignToMe}
                  disabled={assigning || ticket.assigned_to === currentAgent?.id}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-5 py-2.5 rounded-full shadow-sm transition shrink-0"
                >
                  {assigning
                    ? 'Assigning...'
                    : ticket.assigned_to === currentAgent?.id
                    ? 'Assigned to you'
                    : ticket.assigned_agent?.name
                    ? 'Reassign to me'
                    : 'Assign to me'}
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-xs font-semibold px-5 py-2.5 rounded-full shadow-sm transition shrink-0"
                >
                  Assign agent ▾
                </button>
              )}
            </div>

            {/* Internal remarks (staff only) */}
            <div className="bg-[#FFFBEB] border border-amber-100 rounded-2xl shadow-sm p-5 mb-5">
              <p className="text-xs font-bold text-amber-700 mb-2.5">Internal remarks (staff only)</p>
              <div className="flex flex-col gap-3 mb-3">
            {(ticket.internal_remarks || remarks).map((r) => {
  const mine = r.author?.id === currentAgent?.id && r.author?.role === currentAgent?.role;
  const name = r.author?.name || 'Staff';
  return (
    <div key={r.id} className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
      <div className={`shrink-0 w-7 h-7 rounded-full text-white text-[10px] font-semibold flex items-center justify-center ${mine ? 'bg-amber-600' : 'bg-amber-400'}`}>
        {name.trim().charAt(0).toUpperCase()}
      </div>
      <div className={`max-w-[80%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
        <p className="text-[10px] text-amber-700/70 mb-0.5 px-1">{mine ? 'You' : name}</p>
        <div
          className={`px-3.5 py-2 text-sm shadow-sm ${
            mine
              ? 'bg-amber-600 text-white rounded-2xl rounded-br-sm'
              : 'bg-white text-gray-700 border border-amber-100 rounded-2xl rounded-bl-sm'
          }`}
        >
          {r.message}
        </div>
      </div>
    </div>
  );
})}
                {(ticket.internal_remarks || remarks).length === 0 && (
                  <p className="text-xs text-gray-400">No internal remarks yet.</p>
                )}
              </div>
              <form onSubmit={handleAddRemark} className="flex items-end gap-2.5">
                <textarea
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  rows={2}
                  placeholder="Add a note visible only to staff..."
                  className="flex-1 border border-amber-200 rounded-xl px-3 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none"
                />
                <button
                  type="submit"
                  disabled={addingRemark}
                  className="bg-amber-600 hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-sm transition shrink-0"
                >
                  {addingRemark ? '...' : 'Add'}
                </button>
              </form>
            </div>

{/* Conversation */}
<div className="bg-white border border-gray-100 rounded-2xl shadow-sm mb-5 overflow-hidden">
  <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
    <p className="text-xs font-semibold text-gray-600">Conversation</p>
    <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
      {ticket.replies?.length || 0} messages
    </span>
  </div>

  <div className="p-5 flex flex-col gap-4 max-h-[420px] overflow-y-auto">
    {ticket.replies?.map((r) => {
      const mine = r.sender?.id === currentAgent?.id && r.sender_role === currentAgent?.role;
      const name = r.sender?.name || 'User';
      return (
        <div key={r.id} className={`flex items-end gap-2.5 ${mine ? 'flex-row-reverse' : ''}`}>
          <div className={`shrink-0 w-8 h-8 rounded-full text-white text-xs font-semibold flex items-center justify-center ${mine ? 'bg-gradient-to-br from-purple-600 to-pink-500' : 'bg-gray-400'}`}>
            {name.trim().charAt(0).toUpperCase()}
          </div>
          <div className={`max-w-[75%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
            <p className="text-[10px] text-gray-400 mb-1 px-1">
              {mine ? 'You' : name}
              {r.created_at && ` · ${new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
            </p>
            <div
              className={`px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                mine
                  ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-2xl rounded-br-sm'
                  : 'bg-purple-50/70 text-gray-700 border border-purple-100 rounded-2xl rounded-bl-sm'
              }`}
            >
              {r.message}
            </div>
          </div>
        </div>
      );
    })}
    {ticket.replies?.length === 0 && (
      <div className="text-center py-8">
        <p className="text-2xl mb-1">💬</p>
        <p className="text-xs text-gray-400">No replies yet. Start the conversation below.</p>
      </div>
    )}
  </div>

  <form onSubmit={handleReply} className="border-t border-gray-100 bg-gray-50/60 p-4 flex items-end gap-3">
    <textarea
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      rows={2}
      placeholder="Type your reply..."
      className="flex-1 border border-gray-200 rounded-2xl px-4 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
    />
    <button
      type="submit"
      disabled={sending || !message.trim()}
      className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 rounded-full shadow-sm transition shrink-0"
    >
      {sending ? '...' : 'Send ➤'}
    </button>
  </form>
</div>
          </>
        )}
    </div>
    </div>

    {showAssignModal && (
      <div
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        onClick={() => setShowAssignModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Assign ticket</h3>
          <p className="text-xs text-gray-500 mb-4">{ticket?.ticket_number}</p>
          <select
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">Select an agent...</option>
            {allAgents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowAssignModal(false)}
              className="text-xs font-semibold text-gray-500 px-4 py-2 rounded-full hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleModalAssign}
              disabled={!selectedAgentId || modalAssigning}
              className="text-xs font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 px-5 py-2 rounded-full shadow-sm disabled:opacity-50 hover:opacity-90 transition"
            >
              {modalAssigning ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    )}
    </Wrapper>
  );
}